// The interface language on the gov.md bar: three controls, all visible.
//
// It used to be a `MudSelect` labelled "Română" — a control whose own label
// was the answer it was asking for. The drawing has plain inline buttons on the
// strip (`wireframes/react/src/pages/Autentificare.tsx:39`), so that is what this
// is now.
//
// Every route uses it, because the strip it sits on is on every route (owner
// ruling 2026-09-09). It is also the only language control in the system now:
// the globe that used to sit beside the identity in the sidebar is gone, so
// there is one list, read from `SUPPORTED_LOCALES`, in one place.
//
// The strip writes the two-letter code, not the full name: MUD's `.top-section`
// draws `Ro · Ru · En` (Components `KB1Z6GHjOhHw3QL8ki9Mn6`, node `1144:15205`)
// and so does the drawing (`Autentificare.tsx:38`). The full name stays as each
// control's accessible name — a screen reader should say "Română", not "Ro" —
// so the visible label shortens without the announcement following it down.
// Each language is still written in its own language and script.
// per docs/ui-shape-govmd-bar.md §The languages
import { useTolgee, useTranslate } from '@tolgee/react';
import { MudButton } from '@egov-moldova/mud-react';
import type { Locale } from '@simsm/shared-types';
import {
  SUPPORTED_LOCALES,
  LOCALE_LABELS,
  LOCALE_CODE_LABELS,
  DEFAULT_LOCALE,
} from '@simsm/ui-i18n';

// The drawn order — Ro, Ru, En (`wireframes/react/src/pages/Autentificare.tsx:39`)
// — not the registry's. `SUPPORTED_LOCALES` stays the source of which languages
// exist; the `Record` makes a fourth one a compile error here rather than a
// silent omission from the bar.
const DRAWN_ORDER: Record<Locale, number> = { ro: 0, ru: 1, en: 2 };
const BAR_LOCALES = [...SUPPORTED_LOCALES].sort((a, b) => DRAWN_ORDER[a] - DRAWN_ORDER[b]);

export function LanguageSwitcher(): JSX.Element {
  const { t } = useTranslate();
  const tolgee = useTolgee(['language']);
  const currentLanguage = tolgee.getLanguage() ?? DEFAULT_LOCALE;

  return (
    // `group`, not `radiogroup`: its children would have to be `role="radio"`,
    // which R2 reserves for `MudRadio` — a form control with a dot, not a
    // language chip.
    <div className="simsm-langswitch" role="group" aria-label={t('common.locale.label')}>
      {BAR_LOCALES.map((locale) => {
        const active = locale === currentLanguage;
        return (
          <MudButton
            key={locale}
            className={
              active
                ? 'simsm-langswitch__item simsm-langswitch__item--active'
                : 'simsm-langswitch__item'
            }
            data-locale={locale}
            aria-current={active ? 'true' : undefined}
            aria-label={LOCALE_LABELS[locale]}
            shape="rectangular"
            variant="strict"
            appearance="text"
            size="sm"
            onClick={() => void tolgee.changeLanguage(locale)}
          >
            {LOCALE_CODE_LABELS[locale]}
          </MudButton>
        );
      })}
    </div>
  );
}
